import React, { createContext, useContext, useState, useCallback } from "react";
import Toast, {
  ToastAction,
  ToastSeverity,
} from "../components/common/feedback/Toast";

interface ToastContextType {
  showToast: (
    message: string,
    severity?: ToastSeverity,
    duration?: number,
  ) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  /** Erfolgsmeldung mit „Rückgängig“ – für Aktionen, die ohne Rückfrage sofort wirken */
  showUndo: (message: string, onUndo: () => void) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: React.ReactNode;
}

/**
 * Toast Provider - Stellt globale Toast-Funktionalität bereit
 *
 * Usage:
 * 1. In App.tsx einbinden: <ToastProvider><App /></ToastProvider>
 * 2. In Komponenten verwenden: const { showSuccess } = useToast();
 *
 * Features:
 * - Verschiedene Toast-Typen: success, error, warning, info
 * - Automatische Dauer-Anpassung je nach Typ
 * - Globale Verfügbarkeit in der gesamten App
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<ToastSeverity>("success");
  const [duration, setDuration] = useState(2000);
  const [action, setAction] = useState<ToastAction | undefined>();

  const showToast = useCallback(
    (msg: string, sev: ToastSeverity = "success", dur: number = 2000) => {
      setMessage(msg);
      setSeverity(sev);
      setDuration(dur);
      setAction(undefined);
      setOpen(true);
    },
    [],
  );

  const showUndo = useCallback((msg: string, onUndo: () => void) => {
    setMessage(msg);
    setSeverity("success");
    // Länger als eine einfache Erfolgsmeldung – Zeit genug, sich anders zu entscheiden.
    setDuration(6000);
    setAction({ label: "Rückgängig", onClick: onUndo });
    setOpen(true);
  }, []);

  const showSuccess = useCallback(
    (msg: string) => {
      showToast(msg, "success", 2000);
    },
    [showToast],
  );

  const showError = useCallback(
    (msg: string) => {
      showToast(msg, "error", 4000); // Fehler länger anzeigen
    },
    [showToast],
  );

  const showWarning = useCallback(
    (msg: string) => {
      showToast(msg, "warning", 3000);
    },
    [showToast],
  );

  const showInfo = useCallback(
    (msg: string) => {
      showToast(msg, "info", 2000);
    },
    [showToast],
  );

  const handleClose = () => {
    setOpen(false);
  };

  const value: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showUndo,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast
        open={open}
        message={message}
        severity={severity}
        duration={duration}
        action={action}
        onClose={handleClose}
      />
    </ToastContext.Provider>
  );
};

/**
 * Hook zum Anzeigen von Toasts
 *
 * @throws Error wenn außerhalb eines ToastProviders verwendet
 *
 * @example
 * const { showSuccess, showError } = useToast();
 * showSuccess('Erfolgreich gespeichert');
 * showError('Fehler beim Speichern');
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
